// Requests: everything sent to the assistant, how it classified it, and what happened. The waiting cards on the
// left (source, when, status, title, where it goes), the selected one on the right with what came in (the email,
// the conversation) and what the assistant proposes. Context suggestions are optional: one collapsible group per
// space below the waiting cards, not counted in Waiting, open or closed remembered per person.
//
// Approve and Ignore resolve in place, then the row collapses and the list closes the gap. Keys: J and K (or the
// arrows) move, A approves, I ignores, C goes to the change box. The phone has its own Requests (phone/Requests.tsx).
import { memo, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { can, EmailOrigin, isSuggestion, PLACE_FIRST, type MeetingMinutes, type ProposedAction } from '@dpw/shared';
import { MinutesBody } from '../meetings/parts';
import { api } from '../api';
import { useActions, useActivity, useMe, useOpenActivities, usePrefs, useUpdatePrefs } from '../lib/queries';
import { useCardChange, useDecide } from '../lib/decide';
import { useHotkeys } from '../lib/keys';
import { useLinger } from '../lib/linger';
import { listItem, spring } from '../lib/motion';
import { arrived, bytes, dayLabel, localDay, localTime } from '../lib/format';
import { useToast } from '../ui/toast';
import { usePhone } from '../lib/media';
import { PhoneRequests } from '../phone/Requests';
import { ApproveCard, STATUS_WORD } from '../ui/ApproveCard';
import { ErrorNote } from '../ui/bits';
import { ForwardHelp, useAllTracks } from '../ui/email';
import { Count, DrawnCheck, Orb, SkelList, Skel } from '../ui/motion';
import { IconCalendar, IconChat, IconClip, IconClock, IconMail, IconRec } from '../ui/icons';

export const ICON: Record<ProposedAction['source'], () => ReactElement> = {
  chat: () => <IconChat />, email: () => <IconMail />, recording: () => <IconRec />, notetaker: () => <IconRec />, gone_quiet: () => <IconClock />,
  invite: () => <IconCalendar />, team_check: () => <IconChat />, minutes: () => <IconRec />, milestone: () => <IconCalendar />,
  activity_update: () => <IconChat />, logged_email: () => <IconMail />, new_contact: () => <IconChat />, follow_up: () => <IconClock />,
};
export const tone = (a: ProposedAction) => a.status === 'approved' ? 'grn' : a.status !== 'waiting' ? 'neu'
  : a.source === 'gone_quiet' ? 'red' : a.source === 'notetaker' || a.source === 'follow_up' ? 'amb' : 'acc';
const PILL: Record<ProposedAction['status'], string> = { waiting: 'pl amb', approved: 'pl grn', ignored: 'pl', rejected: 'pl' };
const row = (a: ProposedAction, label: string) => a.rows.find(r => r.label === label)?.value ?? null;
/** Where it goes and what it becomes: "AI Office · Data Readiness · New activity". */
export const classify = (a: ProposedAction) => [row(a, 'Space and track') ?? row(a, 'Context') ?? a.spaceName, a.kindLabel].filter(Boolean).join(' · ');
export const whenOf = (iso: string) => arrived(iso);


export function RequestsPage() {
  return usePhone() ? <PhoneRequests /> : <DesktopRequests />;
}

function DesktopRequests() {
  const [status, setStatus] = useState<'waiting' | 'all'>('waiting');
  const me = useMe();
  const director = !!me.data && can(me.data.role, 'space.all');
  const [params] = useSearchParams();
  // for=everyone: a link to someone else's card (the director's, from Settings).
  const [forWhom, setForWhom] = useState<'me' | 'everyone'>(params.get('for') === 'everyone' ? 'everyone' : 'me');
  const who = director ? forWhom : 'me';
  const q = useActions(status, who);
  const waitingQ = useActions('waiting', who);
  const allQ = useActions('all', who);
  const decide = useDecide();
  const [sel, setSel] = useState<string | undefined>(params.get('id') ?? undefined);

  const data = useMemo(() => q.data ?? [], [q.data]);
  // Optional cards that are still waiting live in their space's group; everything else is a row.
  const rows0 = useMemo(() => data.filter(a => !(isSuggestion(a) && a.status === 'waiting')), [data]);
  const rows = useLinger(rows0, a => status === 'waiting' && a.status !== 'waiting');
  const groups = useMemo(() => {
    const m = new Map<string, { spaceId: string; spaceName: string; cards: ProposedAction[] }>();
    for (const a of data) {
      if (!isSuggestion(a) || (a.status !== 'waiting' && status === 'all')) continue;
      const k = a.spaceId ?? 'none';
      if (!m.has(k)) m.set(k, { spaceId: k, spaceName: a.spaceName ?? 'No space', cards: [] });
      m.get(k)!.cards.push(a);
    }
    return [...m.values()].sort((x, y) => x.spaceName.localeCompare(y.spaceName));
  }, [data, status]);
  const waitingCount = (waitingQ.data ?? []).filter(a => !isSuggestion(a)).length;

  // Keep a copy of the selected card, so its result stays on screen after its row leaves.
  const last = useRef<ProposedAction | undefined>(undefined);
  const found = rows.find(a => a.id === sel);
  if (found) last.current = found;
  const cur = found ?? (last.current?.id === sel ? last.current : undefined);
  // Nothing (or something gone) selected: the first row; after a row leaves, the one that took its place.
  const index = useRef(0);
  // A card asked for in the address (a link from Record or a meeting) may be newer than the list on screen: wait for
  // the list to refresh before falling back to the first row.
  const asked = useRef(params.get('id'));
  useEffect(() => {
    if (q.isLoading) return;
    if (asked.current && sel === asked.current && !rows.some(a => a.id === sel) && q.isFetching) return;
    asked.current = null;
    const i = rows.findIndex(a => a.id === sel);
    if (i >= 0) { index.current = i; return; }
    if (cur && rows0.some(a => a.id === cur.id)) return;
    const next = rows[Math.min(index.current, rows.length - 1)];
    if (next?.id !== sel) setSel(next?.id);
  }, [rows, rows0, sel, cur, q.isLoading, q.isFetching]);
  // The open card is kept in the address (a reload or a shared link opens it) without a router navigation: that would
  // re-render the whole app on every pick. Only while the address is still this page: a pick that lands just after
  // a jump elsewhere (the command palette) must not write its id onto the next page's address.
  const { pathname } = useLocation();
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.pathname !== pathname) return;
    if (sel && u.searchParams.get('id') !== sel) { u.searchParams.set('id', sel); window.history.replaceState(window.history.state, '', u); }
  }, [sel, pathname]);

  const move = (d: number) => {
    const i = rows.findIndex(a => a.id === sel);
    const n = rows[Math.max(0, Math.min(rows.length - 1, (i < 0 ? 0 : i + d)))];
    if (n) { setSel(n.id); document.querySelector(`[data-row="${n.id}"]`)?.scrollIntoView({ block: 'nearest' }); }
  };
  const can2 = (a?: ProposedAction): a is ProposedAction => !!a && a.status === 'waiting';
  useHotkeys({
    j: () => move(1), k: () => move(-1), ArrowDown: () => move(1), ArrowUp: () => move(-1),
    a: () => { if (can2(cur) && cur.kind !== 'confirm_answer' && cur.kind !== 'review_answer' && !PLACE_FIRST.has(cur.kind)) decide.approve(cur); },
    i: () => { if (can2(cur) && cur.kind !== 'confirm_answer' && cur.kind !== 'review_answer') decide.ignore(cur); },
    c: () => document.querySelector<HTMLInputElement>('.det [data-change], [role="dialog"] [data-change]')?.focus(),
  });

  const loading = q.isLoading;
  return (
    <>
      <div className="hdr">
        <div className="htl"><h1>Requests</h1><span className="xs mut">Everything sent to the assistant, how it classified it, and what happened</span></div>
        <div className="hact">
          {director && (
            <div className="seg2" role="group" aria-label="Whose requests">
              <button className={forWhom === 'me' ? 'on' : ''} aria-pressed={forWhom === 'me'} onClick={() => setForWhom('me')}>Mine</button>
              <button className={forWhom === 'everyone' ? 'on' : ''} aria-pressed={forWhom === 'everyone'} onClick={() => setForWhom('everyone')}>Everyone's</button>
            </div>
          )}
          <div className="seg2" role="group" aria-label="Show">
            <button className={status === 'waiting' ? 'on' : ''} aria-pressed={status === 'waiting'} onClick={() => setStatus('waiting')}>Waiting <Count className="mono xs" value={waitingCount} /></button>
            <button className={status === 'all' ? 'on' : ''} aria-pressed={status === 'all'} onClick={() => setStatus('all')}>All <Count className="mono xs" value={allQ.data?.length ?? 0} /></button>
          </div>
        </div>
      </div>
      <div className="ibx">
        <div className="list">
          {loading ? <SkelList rows={5} /> : (
            <LayoutGroup>
              <AnimatePresence mode="popLayout" initial={false}>
                {rows.map((a, i) => <ListRow key={a.id} a={a} at={i} on={a.id === sel} mine={a.owner.id === me.data?.id} onPick={setSel} />)}
                {!rows.length && (
                  <motion.div key="empty" layout className="panel empty0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <b>Nothing is waiting for you.</b>
                    <span className="xs mut">New requests land here when you forward an email, record a meeting, or the assistant needs a decision.</span>
                  </motion.div>
                )}
                {groups.map(g => <motion.div key={`g-${g.spaceId}`} layout variants={listItem} initial="initial" animate="animate" exit="exit"><ContextGroup g={g} linger={status === 'waiting'} /></motion.div>)}
                <motion.div key="log" layout className="panel" style={{ padding: '12px 14px' }}>
                  <ForwardHelp>{(addr, on) => <div className="xs mut">Forward any email to {addr}, or copy it in. The assistant reads it, proposes where it belongs, and you approve it here, on Today, or from the notification.{on ? '' : ' Not connected on this server yet.'}</div>}</ForwardHelp>
                </motion.div>
              </AnimatePresence>
            </LayoutGroup>
          )}
          <div className="keys xs mut" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> move <kbd>A</kbd> approve <kbd>I</kbd> ignore <kbd>C</kbd> change</div>
        </div>
        <div className="det">
          {loading ? <DetailSkeleton /> : cur ? <Detail a={cur} key={cur.id} /> : null}
        </div>
      </div>
    </>
  );
}

/**
 * One row of the list. Memoised: picking a card re-renders only the two rows whose selection changed, not every row
 * (each row is a layout-animated motion element, and re-rendering them all made picking a card slow). Its layout is
 * measured only when its place in the list (at) changes: in a LayoutGroup, any member re-rendering otherwise makes
 * Motion measure every row, the groups and the footer, and picking a card paid for that on every click.
 */
const ListRow = memo(function ListRow({ a, at, on, mine, onPick }: { a: ProposedAction; at: number; on: boolean; mine: boolean; onPick: (id: string) => void }) {
  return (
    <motion.div layout layoutDependency={at} variants={listItem} initial="initial" animate="animate" exit="exit" data-row={a.id}>
      <ReqItem a={a} on={on} mine={mine} onPick={() => onPick(a.id)} />
    </motion.div>
  );
});

function ReqItem({ a, on, mine, onPick }: { a: ProposedAction; on: boolean; mine: boolean; onPick: () => void }) {
  const Ico = ICON[a.source];
  return (
    <button type="button" className={`it${on ? ' on' : ''}${a.status !== 'waiting' ? ' gone' : ''}`} onClick={onPick} aria-current={on ? 'true' : undefined}>
      <motion.span layout="position" className={`mi ${tone(a)}`}><Ico /></motion.span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="rowc btwc" style={{ flexWrap: 'nowrap' }}>
          <span className="xs mut">{a.sourceLabel} · {whenOf(a.createdAt)}</span>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span key={a.status} className={PILL[a.status]} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1, transition: spring.bouncy }} exit={{ opacity: 0 }}>{STATUS_WORD[a.status]}</motion.span>
          </AnimatePresence>
        </span>
        <span className="sm ttl">{a.title}</span>
        <span className="xs cls" title={classify(a)}>{!mine ? `For ${a.owner.name} · ` : ''}{classify(a)}{a.updated ? ' · updated' : ''}</span>
      </span>
    </button>
  );
}

function ContextGroup({ g, linger }: { g: { spaceId: string; spaceName: string; cards: ProposedAction[] }; linger: boolean }) {
  const prefs = usePrefs();
  const update = useUpdatePrefs();
  const open = prefs.data?.openGroups.includes(g.spaceId) ?? false;
  const toggle = () => {
    const cur = prefs.data?.openGroups ?? [];
    update.mutate({ openGroups: open ? cur.filter(x => x !== g.spaceId) : [...cur, g.spaceId] });
  };
  const cards = useLinger(g.cards, a => linger && a.status !== 'waiting');
  const waiting = g.cards.filter(a => a.status === 'waiting').length;
  const body = `cg-${g.spaceId}`;
  return (
    <motion.div layout className="cgrp" transition={spring.gentle} data-group={g.spaceName}>
      <motion.button layout="position" type="button" className={`cgh${open ? ' open' : ''}`} onClick={toggle} aria-expanded={open} aria-controls={body}>
        <motion.svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          animate={{ rotate: open ? 90 : 0 }} transition={spring.snappy}><path d="M9 6l6 6-6 6" /></motion.svg>
        <Orb size={9} />
        <span className="cgtt"><b>Context suggestions</b><em>{g.spaceName}</em></span>
        <span className="cgn" aria-label={`${waiting} waiting`}><Count value={waiting} /></span>
      </motion.button>
      <AnimatePresence initial={false} mode="popLayout">
        {open ? (
          <motion.div key="b" id={body} className="cgb" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }} exit={{ opacity: 0, transition: { duration: 0.12 } }}>
            <AnimatePresence mode="popLayout" initial={false}>
              {cards.map(a => (
                <motion.div key={a.id} layout variants={listItem} initial="initial" animate="animate" exit="exit"><ApproveCard action={a} variant="compact" /></motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div key="s" className="cgs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.1 } }}>
            Lasting facts the assistant noticed. Optional, so they are not counted in Waiting.
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Detail({ a }: { a: ProposedAction }) {
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();
  const email = EmailOrigin.safeParse(a.origin);
  const e = email.success ? email.data : {};
  const hasMail = !!(e.subject || e.body);
  const rec = recordingMinutes(a);
  const from = typeof a.origin.from === 'string' ? a.origin.from : a.owner.id === me.data?.id ? 'You' : a.owner.name;
  const canUndo = a.status === 'ignored' && !!a.decidedAt && Date.now() - new Date(a.decidedAt).getTime() < 10 * 60_000 && a.kind !== 'nudge_check';
  const head = a.status === 'waiting' ? 'Needs approval' : STATUS_WORD[a.status];
  return (
    <motion.div className="panel reqdet" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }}>
      <div className="rowc btwc dh">
        <div style={{ minWidth: 0 }}>
          <div className="k">{a.sourceLabel} · {from} · {whenOf(a.createdAt)}</div>
          <h2>{a.title}</h2>
        </div>
        <span className={PILL[a.status]}>{head}</span>
      </div>
      {hasMail && (
        <div className="mail">
          <div className="mh">
            {e.from && <><b>From</b><span>{e.from}</span></>}
            {e.to && <><b>To</b><span>{e.to}</span></>}
            {e.receivedAt && <><b>Date</b><span>{dayLabel(localDay(e.receivedAt))} {localTime(e.receivedAt)}</span></>}
            {e.subject && <><b>Subject</b><span>{e.subject}</span></>}
          </div>
          {e.body && <div className="mb">{e.body}</div>}
          {!!e.attachments?.length && <div className="atts">{e.attachments.map(x => x.fileId
            ? <a className="att" key={x.fileId} href={api.files.downloadUrl(x.fileId)}><IconClip />{x.name}{x.size ? ` · ${bytes(x.size)}` : ''}</a>
            : <span className="att" key={x.name}><IconClip />{x.name}{x.size ? ` · ${bytes(x.size)}` : ''}</span>)}</div>}
        </div>
      )}
      {a.conversationId && <Link className="xs ulink" to={`/assistant/${a.conversationId}`}>Open the conversation</Link>}
      {e.note && <div className="fwnote"><span className="k">{e.forwardedBy ?? from} wrote</span><span className="sm">{e.note}</span></div>}
      {rec && <div className="mini recmin"><MinutesBody m={rec} />{typeof a.origin.activityId === 'string' && <Link className="xs ulink" to={`/meetings/${a.origin.activityId}`}>Open the meeting, with the recording and transcript</Link>}</div>}
      <ApproveCard action={a} variant="detail" pickers={isRetargetable(a) ? <><EmailTarget a={a} /><EmailExtras a={a} /></> : rec && a.status === 'waiting' ? <EmailExtras a={a} /> : undefined} />
      {canUndo && (
        <div className="rowc"><button type="button" className="bt ghost sm" onClick={() => { void api.actions.restore(a.id).then(() => { toast('Back in Waiting.'); void qc.invalidateQueries(); }, (er: unknown) => toast(er instanceof Error ? er.message : 'Could not undo.')); }}>Undo</button></div>
      )}
    </motion.div>
  );
}

function DetailSkeleton() {
  return (
    <div className="panel reqdet" role="status" aria-label="Loading">
      <Skel w="35%" h={10} /><Skel w="60%" h={20} style={{ marginTop: 6 }} />
      <Skel h={120} r={12} style={{ marginTop: 14 }} />
      <Skel h={90} r={12} style={{ marginTop: 14 }} />
      <div className="rowc" style={{ marginTop: 14 }}><Skel w={110} h={36} r={18} /><Skel w={80} h={36} r={18} /></div>
    </div>
  );
}

/**
 * A forwarded email's parts that can be left out before approving: a change it asks for, a follow-up, an
 * attachment. Each tick saves the card's next version ("Updated"); Approve does what is still ticked.
 */
export function EmailExtras({ a, phone }: { a: ProposedAction; phone?: boolean }) {
  const e = EmailOrigin.safeParse(a.origin);
  const extras = e.success ? e.data.extras ?? [] : [];
  const tick = useCardChange((x: { id: string; include: boolean }) => api.actions.extra(a.id, x.id, x.include));
  if (!extras.length) return null;
  return (
    <div className={phone ? 'ph-grp' : 'xtras'} role="group" aria-label="It will also">
      {extras.map(x => (
        <div className={phone ? 'ph-rowx xtra' : 'lrow xtra'} key={x.id}>
          <button type="button" className={`chk${x.include ? ' on' : ''}`} role="checkbox" aria-checked={x.include} aria-label={x.label}
            disabled={tick.isPending || a.status !== 'waiting'} onClick={() => tick.mutate({ id: x.id, include: !x.include })}><DrawnCheck /></button>
          <span className={`sm${x.include ? '' : ' left'}`} style={{ flex: 1 }}>{x.label}</span>
        </div>
      ))}
      {tick.error ? <ErrorNote error={tick.error} /> : null}
    </div>
  );
}

/** A recording's minutes card: the minutes the assistant wrote (summary, topics, decisions, next steps). */
export function recordingMinutes(a: ProposedAction): MeetingMinutes | null {
  const m = (a.origin as { minutes?: MeetingMinutes }).minutes;
  return a.source === 'recording' && m && typeof m.summary === 'string' ? m : null;
}

/** A forwarded email that becomes an update or a new activity can be pointed elsewhere before approving. */
export const isRetargetable = (a: ProposedAction) => a.status === 'waiting' && (a.source === 'email' || a.source === 'logged_email')
  && (a.kind === 'update_activity' || a.kind === 'create_activity');

/**
 * "Space and track" and "Log as" on a forwarded email (the prototype's pickers). Each change saves the card's next
 * version at once ("Updated"); what the email asked for goes with it. Approve does it.
 */
export function EmailTarget({ a }: { a: ProposedAction }) {
  const tracks = useAllTracks();
  const acts = useOpenActivities();
  const activityId = a.kind === 'update_activity' ? a.payload.activityId : null;
  const current = useActivity(activityId ?? '', !!activityId);
  const trackId = a.kind === 'create_activity' ? a.payload.trackId : current.data?.trackId ?? '';
  const move = useCardChange((b: { trackId: string; activityId: string | null }) => api.actions.retarget(a.id, b));
  const inTrack = (acts.data ?? []).filter(x => x.trackId === trackId);
  // The current activity is listed even if it is done (it is not in the open list).
  if (activityId && current.data && !inTrack.some(x => x.id === activityId)) inTrack.unshift({ ...current.data });
  const groups = new Map<string, { id: string; name: string }[]>();
  for (const t of tracks.data ?? []) groups.set(t.space, [...(groups.get(t.space) ?? []), t]);
  if (!trackId || !tracks.data) return <div className="g2f pick"><Skel h={38} r={10} /><Skel h={38} r={10} /></div>;
  return (
    <div className="g2f pick">
      <label className="fld">Space and track
        <select className="inp" value={trackId} disabled={move.isPending} onChange={e => move.mutate({ trackId: e.target.value, activityId: null })}>
          {[...groups.entries()].map(([space, ts]) => <optgroup key={space} label={space}>{ts.map(t => <option key={t.id} value={t.id}>{space} · {t.name}</option>)}</optgroup>)}
        </select>
      </label>
      <label className="fld">Log as
        <select className="inp" value={activityId ?? ''} disabled={move.isPending} onChange={e => move.mutate({ trackId, activityId: e.target.value || null })}>
          {inTrack.map(x => <option key={x.id} value={x.id}>Update on: {x.title}</option>)}
          <option value="">New activity</option>
        </select>
      </label>
      {move.error ? <ErrorNote error={move.error} /> : null}
    </div>
  );
}
